import React from 'react';
import { Loader2Icon, CheckCircle2Icon, XCircleIcon, LayoutDashboardIcon, AlertTriangleIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card } from '../ui/card';

/**
 * The two dashboard reads: `get_dashboard` (its current panels and filters) and
 * `get_dashboard_data` (one panel's numbers).
 *
 * Lookups, not deliverables — so they get the same card, icon and status line as
 * create_dashboard beside them, but no View button: Ellie reads a dashboard on
 * her way to changing it, and the update step that follows already offers one
 * for the same dashboard.
 *
 * Deliberately unmarked for concise mode, like the other lookups: at low
 * verbosity what matters is what she did, not what she checked first.
 */

interface DashboardReadToolCallProps {
  toolName: string;
  toolCallId: string;
  args: any;
  argsText: string;
  result?: string;
  status?: { type: 'running' | 'complete' | 'incomplete'; reason?: string };
}

function parseJson(value: unknown): any {
  if (value === undefined || value === null) return null;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value as string);
  } catch {
    return null;
  }
}

const count = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

const DashboardReadToolCallComponent: React.FC<DashboardReadToolCallProps> = ({
  toolName,
  args,
  argsText,
  result,
  status,
}) => {
  const isPanelData = toolName === 'get_dashboard_data';
  const isRunning = status?.type === 'running' || (status === undefined && result === undefined);
  const isError = status?.type === 'incomplete' && status.reason === 'error';
  const isComplete = !isRunning && !isError && result !== undefined;

  const parsedArgs = parseJson(args) ?? parseJson(argsText) ?? {};
  const parsedResult = isComplete ? parseJson(result) : null;

  const errorMessage: string | undefined = parsedResult?.error;
  // A panel that was never built is its own outcome, not a failure: nothing was
  // queried, so it reads as a warning rather than an error the user should act
  // on as if something broke.
  const notBuilt = !!parsedResult?.not_built;
  const hasError = isError || (!!errorMessage && !notBuilt) || (isComplete && !parsedResult);

  const label = isPanelData ? 'Read Panel Data' : 'Read Dashboard';

  const summary = (() => {
    if (!parsedResult) return '';
    if (isPanelData) {
      const title = parsedResult.panel_title || parsedArgs?.panel_title;
      return title ? `Read "${title}"` : 'Read panel data';
    }
    const name = parsedResult.name || 'dashboard';
    const parts = [
      Array.isArray(parsedResult.panels) ? count(parsedResult.panels.length, 'panel') : null,
      Array.isArray(parsedResult.filters) && parsedResult.filters.length
        ? count(parsedResult.filters.length, 'filter')
        : null,
    ].filter(Boolean);
    return `Read "${name}"${parts.length ? ` — ${parts.join(', ')}` : ''}`;
  })();

  const runningText = isPanelData
    ? `Reading panel data${parsedArgs?.panel_title ? `: ${parsedArgs.panel_title}` : ''}...`
    : 'Reading dashboard...';

  return (
    <Card>
      <div className={cn(
        'p-3',
        isRunning
          ? 'bg-primary/10 border-b border-primary/20'
          : 'bg-muted/50 border-b border-border'
      )}>
        <div className="flex items-center gap-3">
          <div className="flex-1 space-y-1 min-w-0">
            {isRunning ? (
              <div className="flex items-center gap-2">
                <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
                <p className="text-sm m-0 text-foreground truncate">{runningText}</p>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <LayoutDashboardIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <p className="text-sm m-0 text-muted-foreground">{label}</p>
                </div>
                {hasError ? (
                  <div className="flex items-center gap-2">
                    <XCircleIcon className="h-4 w-4 text-destructive flex-shrink-0" />
                    <p className="text-sm m-0 text-destructive">
                      {errorMessage || `Failed to ${isPanelData ? 'read panel data' : 'read dashboard'}`}
                    </p>
                  </div>
                ) : notBuilt ? (
                  <div className="flex items-center gap-2">
                    <AlertTriangleIcon className="h-4 w-4 text-warning flex-shrink-0" />
                    <p className="text-sm m-0 text-foreground truncate">
                      {parsedResult?.panel_title
                        ? `"${parsedResult.panel_title}" hasn't been built yet`
                        : "This panel hasn't been built yet"}
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <CheckCircle2Icon className="h-4 w-4 text-success flex-shrink-0" />
                    <p className="text-sm m-0 text-foreground truncate">{summary}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export const DashboardReadToolCall = DashboardReadToolCallComponent as React.ComponentType<any>;
